import { PartyPopper, X } from "lucide-react";
import { Link } from "react-router";

import { Button, cx } from "@/components/ui";
import { nextStep, stepsDone, type ChecklistStep } from "@/lib/onboarding";

import { useOnboarding } from "./OnboardingProvider";

const CARD = "flex w-full flex-col gap-2 rounded-xl bg-chrome-raised p-3.5 text-left";
const ICON_BUTTON = "rounded p-0.5 text-chrome-fg-subtle transition-colors hover:text-chrome-fg";

/**
 * The sidebar's "Getting started": the steps this person's role covers, worked out from what the business already
 * has. It can be hidden (the X) and brought back from the help menu, and when the last step is ticked it says so
 * once. Nothing is drawn for a role with nothing to do, while loading, or when it is hidden.
 */
export function ChecklistCard({ onNavigate }: { onNavigate?: () => void }) {
  const onboarding = useOnboarding();
  if (!onboarding || !onboarding.applicable || onboarding.dismissed) return null;

  const { steps } = onboarding;
  const next = nextStep(steps);
  if (!next) {
    return onboarding.celebrating ? (
      <section aria-label="Getting started" data-tour="checklist" className={CARD}>
        <div className="flex w-full items-center gap-2">
          <PartyPopper className="size-5 shrink-0 text-[#12a97e] motion-safe:animate-pulse" aria-hidden />
          <p role="status" className="flex-1 text-sm leading-5 font-semibold text-chrome-fg">
            You&apos;re all set. Nice work!
          </p>
          <Button
            unstyled
            type="button"
            onClick={onboarding.endCelebration}
            aria-label="Close"
            className={ICON_BUTTON}
          >
            <X className="size-3.5" aria-hidden />
          </Button>
        </div>
      </section>
    ) : null;
  }

  const done = stepsDone(steps);
  const className = "text-xs leading-4 text-chrome-fg-muted transition-colors hover:text-chrome-fg";
  return (
    <section aria-label="Getting started" data-tour="checklist" className={CARD}>
      <div className="flex w-full items-center gap-1.5">
        <h2 className="text-sm leading-5 font-semibold text-chrome-fg">Getting started</h2>
        <span className="flex-1" />
        <span className="text-xs leading-4 text-chrome-fg-subtle">
          {done} of {steps.length}
        </span>
        <Button
          unstyled
          type="button"
          onClick={onboarding.dismiss}
          aria-label="Hide the getting started checklist"
          className={ICON_BUTTON}
        >
          <X className="size-3.5" aria-hidden />
        </Button>
      </div>
      <Progress steps={steps} />
      {next.external ? (
        <a href={next.to} className={className}>
          Next: {next.next}
        </a>
      ) : (
        <Link to={next.to} onClick={onNavigate} className={className}>
          Next: {next.next}
        </Link>
      )}
    </section>
  );
}

/** The bar for the eye, and the list for a screen reader: each step and whether it is done. */
function Progress({ steps }: { steps: readonly ChecklistStep[] }) {
  return (
    <>
      <span aria-hidden className="flex h-1.5 w-full gap-[3px]">
        {steps.map((s) => (
          <span
            key={s.id}
            className={cx("h-1.5 flex-1 rounded-[3px]", s.done ? "bg-[#12a97e]" : "bg-[#443e3b]")}
          />
        ))}
      </span>
      <ul className="sr-only">
        {steps.map((s) => (
          <li key={s.id}>
            {LABELS[s.id]}: {s.done ? "done" : "to do"}
          </li>
        ))}
      </ul>
    </>
  );
}

const LABELS: Record<ChecklistStep["id"], string> = {
  account: "Create your account",
  "verify-email": "Verify your email address",
  "create-website": "Create your website",
  "publish-website": "Publish your website",
  "connect-domain": "Connect your domain",
  "invite-teammate": "Invite a teammate",
};
